import { Alert, MenuItem, Stack, TextField } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import type { BranchPriceItem } from '@tiles-erp/shared-types';
import { PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { ListExportButtons, type ExportColumn } from '../../components/ListExportButtons';
import { useSessionBranchId } from '../../lib/session-branch';
import { ProductFilterBar } from '../products/ProductFilterBar';
import type { ProductFilters } from '../products/api';
import { useBranches, useBranchPrices } from '../products/branch-prices-api';

type ExportRow = BranchPriceItem & {
  mrpPcs: number;
  landingExGst: number;
  landingPcs: number;
  displayExGst: number;
  displayPcs: number;
  actualExGst: number;
  actualPcs: number;
  minimumExGst: number;
  minimumPcs: number;
  franchiseeExGst: number;
  franchiseePcs: number;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;
const ratePerPiece = (rate: number | null, piecesPerBox: number): number =>
  rate === null || piecesPerBox <= 0 ? 0 : round2(rate / piecesPerBox);
const excludingGst = (rate: number | null, gstRate: number): number => {
  if (rate === null) return 0;
  return round2(rate / (1 + Math.max(0, gstRate) / 100));
};
const price = (value: unknown): string => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

const toExportRow = (item: BranchPriceItem): ExportRow => {
  const landingExGst = excludingGst(item.landingCost, item.gstRate);
  const displayExGst = excludingGst(item.displayPrice, item.gstRate);
  const actualExGst = excludingGst(item.sellingPrice, item.gstRate);
  const minimumExGst = excludingGst(item.minSellingPrice, item.gstRate);
  const franchiseeExGst = excludingGst(item.franchiseeRate, item.gstRate);
  return {
    ...item,
    mrpPcs: ratePerPiece(item.mrp, item.piecesPerBox),
    landingExGst,
    landingPcs: ratePerPiece(landingExGst, item.piecesPerBox),
    displayExGst,
    displayPcs: ratePerPiece(displayExGst, item.piecesPerBox),
    actualExGst,
    actualPcs: ratePerPiece(actualExGst, item.piecesPerBox),
    minimumExGst,
    minimumPcs: ratePerPiece(minimumExGst, item.piecesPerBox),
    franchiseeExGst,
    franchiseePcs: ratePerPiece(franchiseeExGst, item.piecesPerBox),
  };
};

const exportColumns: ExportColumn<BranchPriceItem>[] = [
  { header: 'SixOrbit variation ID', value: (row) => row.sixorbitVariationId ?? '', width: 125 },
  { header: 'SixOrbit variation no', value: (row) => row.sixorbitVariationNumber ?? '', width: 125 },
  { header: 'SKU', value: (row) => row.sku, width: 90 },
  { header: 'Product name', value: (row) => row.name, width: 210 },
  { header: 'Brand', value: (row) => row.brandName, width: 110 },
  { header: 'Category', value: (row) => row.categoryName, width: 150 },
  { header: 'Size', value: (row) => row.sizeMm ?? '', width: 75 },
  { header: 'Pcs/box', value: (row) => row.piecesPerBox, width: 65 },
  { header: 'Stock (pcs)', value: (row) => row.stockPcs, width: 80 },
  { header: 'MRP/box', value: (row) => row.mrp ?? 0, width: 85 },
  { header: 'MRP/pc', value: (row) => ratePerPiece(row.mrp, row.piecesPerBox), width: 75 },
  { header: 'Landing/box ex GST', value: (row) => excludingGst(row.landingCost, row.gstRate), width: 100 },
  { header: 'Landing/pc ex GST', value: (row) => ratePerPiece(excludingGst(row.landingCost, row.gstRate), row.piecesPerBox), width: 90 },
  { header: 'Display/box ex GST', value: (row) => excludingGst(row.displayPrice, row.gstRate), width: 100 },
  { header: 'Display/pc ex GST', value: (row) => ratePerPiece(excludingGst(row.displayPrice, row.gstRate), row.piecesPerBox), width: 90 },
  { header: 'Actual/box ex GST', value: (row) => excludingGst(row.sellingPrice, row.gstRate), width: 100 },
  { header: 'Actual/pc ex GST', value: (row) => ratePerPiece(excludingGst(row.sellingPrice, row.gstRate), row.piecesPerBox), width: 90 },
  { header: 'Minimum/box ex GST', value: (row) => excludingGst(row.minSellingPrice, row.gstRate), width: 110 },
  { header: 'Minimum/pc ex GST', value: (row) => ratePerPiece(excludingGst(row.minSellingPrice, row.gstRate), row.piecesPerBox), width: 100 },
  { header: 'Franchisee/box ex GST', value: (row) => excludingGst(row.franchiseeRate, row.gstRate), width: 120 },
  { header: 'Franchisee/pc ex GST', value: (row) => ratePerPiece(excludingGst(row.franchiseeRate, row.gstRate), row.piecesPerBox), width: 110 },
];

/** Branch product master prepared for export to SixOrbit, with box and per-piece rates. */
export function ProductMasterExportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 50 });
  const branches = useBranches();
  const [branchId, setBranchId] = useSessionBranchId();
  const [filters, setFilters] = useState<ProductFilters>({});
  const [error, setError] = useState<string | null>(null);
  const prices = useBranchPrices(branchId || undefined, pagination.query, filters);
  const rows = useMemo(() => (prices.data?.items ?? []).map(toExportRow), [prices.data?.items]);

  const columns = useMemo<ColDef<ExportRow>[]>(() => [
    { field: 'sixorbitVariationId', headerName: 'SixOrbit variation ID', minWidth: 175, pinned: 'left' },
    { field: 'sixorbitVariationNumber', headerName: 'SixOrbit variation no', minWidth: 175, pinned: 'left' },
    { field: 'sku', headerName: 'SKU', minWidth: 125, pinned: 'left' },
    { field: 'name', headerName: 'Product name', minWidth: 220, pinned: 'left' },
    { field: 'brandName', headerName: 'Brand', minWidth: 125 },
    { field: 'categoryName', headerName: 'Category', minWidth: 165 },
    { field: 'sizeMm', headerName: 'Size', width: 100 },
    { field: 'piecesPerBox', headerName: 'Pcs/box', width: 95 },
    { field: 'stockPcs', headerName: 'Stock (pcs)', width: 115 },
    { field: 'mrp', headerName: 'MRP/box ₹', width: 120, valueFormatter: (p) => price(p.value) },
    { field: 'mrpPcs', headerName: 'MRP/pc ₹', width: 110, valueFormatter: (p) => price(p.value) },
    { field: 'landingExGst', headerName: 'Landing/box ex GST ₹', width: 165, valueFormatter: (p) => price(p.value) },
    { field: 'landingPcs', headerName: 'Landing/pc ex GST ₹', width: 155, valueFormatter: (p) => price(p.value) },
    { field: 'displayExGst', headerName: 'Display/box ex GST ₹', width: 165, valueFormatter: (p) => price(p.value) },
    { field: 'displayPcs', headerName: 'Display/pc ex GST ₹', width: 155, valueFormatter: (p) => price(p.value) },
    { field: 'actualExGst', headerName: 'Actual/box ex GST ₹', width: 165, valueFormatter: (p) => price(p.value) },
    { field: 'actualPcs', headerName: 'Actual/pc ex GST ₹', width: 155, valueFormatter: (p) => price(p.value) },
    { field: 'minimumExGst', headerName: 'Minimum/box ex GST ₹', width: 175, valueFormatter: (p) => price(p.value) },
    { field: 'minimumPcs', headerName: 'Minimum/pc ex GST ₹', width: 165, valueFormatter: (p) => price(p.value) },
    { field: 'franchiseeExGst', headerName: 'Franchisee/box ex GST ₹', width: 185, valueFormatter: (p) => price(p.value) },
    { field: 'franchiseePcs', headerName: 'Franchisee/pc ex GST ₹', width: 175, valueFormatter: (p) => price(p.value) },
  ], []);

  const params = {
    branchId: branchId || undefined,
    search: pagination.query.search,
    brandId: filters.brandId,
    categoryId: filters.categoryId,
    seriesId: filters.seriesId,
    sizeMm: filters.sizeMm,
  };

  return <PageContainer
    title="SixOrbit product master export"
    subtitle="MRP remains GST-inclusive. All other box and per-piece rates are shown excluding GST."
    actions={branchId ? <ListExportButtons
      path="/branch-prices"
      params={params}
      columns={exportColumns}
      title={`SixOrbit product master — ${branches.data?.find((branch) => branch.id === branchId)?.name ?? 'Branch'}`}
      filename="sixorbit-product-master"
      onError={setError}
    /> : undefined}
  >
    <Stack spacing={1.5}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField select label="Branch" size="small" value={branchId} sx={{ width: 220 }}
          onChange={(event) => { setBranchId(event.target.value); pagination.setPage(1); }}>
          {(branches.data ?? []).map((branch) => <MenuItem key={branch.id} value={branch.id}>
            {branch.name}{branch.code ? ` (${branch.code})` : ''}
          </MenuItem>)}
        </TextField>
        <ProductFilterBar value={filters} showSeries onChange={(next) => {
          setFilters(next);
          pagination.setPage(1);
        }} />
      </Stack>
      {!branchId
        ? <Alert severity="info">Select a branch to prepare its SixOrbit product master.</Alert>
        : <DataTable
            rows={rows}
            columns={columns}
            meta={prices.data?.meta}
            pagination={pagination}
            loading={prices.isFetching}
            searchPlaceholder="Search by product name or SKU…"
            height={620}
            exportable={false}
            gridOptions={{ getRowId: (params) => params.data.productId }}
          />}
    </Stack>
  </PageContainer>;
}
